import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { CREATE_NEW_PORTFOLIO_ID, PortfolioPickerOption } from 'src/components/global/CreateNewPortfolioModal'
import {
  IGetSharedPortfoliosForMyOrganizationQuery,
  ISharedPortfolioMembershipItemFragment,
  ISharedPortfolioSolutionItemFragment,
  ISolutionListItemFragment,
  useGetSharedPortfoliosForMyOrganizationLazyQuery,
  useSearchSolutionsLazyQuery,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { AppState } from 'src/store/types'
import { isAccessibleSharedPortfolioSolution, resolveFollowTypeLabel } from 'src/utils/sharedPortfolioMembership'

type SharedPortfolioListItem =
  IGetSharedPortfoliosForMyOrganizationQuery['sharedPortfoliosForMyOrganization']['portfolios'][number]

type PortfolioRow = {
  kind: 'private' | 'shared'
  id: string
  name: string
  slug: string
  shared_portfolio_memberships?: ISharedPortfolioMembershipItemFragment[]
  shared_portfolio_solutions?: ISharedPortfolioSolutionItemFragment[]
}

export type SharedPortfolioSolutionEntry = {
  solutionId: string
  id: string
  followingType: string
  followingSolutionNote?: string | null
  members: string
  name: string
  organizationId: string | null
  portfolioId: string | null
  sharedPortfolio: string | null
  slug: string
}

export function useSolutionDetailFollow(solutionDocumentId?: string | null, routerReady = true) {
  const authInfo = useSelector((state: AppState) => state.authInfo)
  const { status } = useAuthenticated()
  const hasPremiumOrganization = authInfo?.premiumOrganizationId != null && authInfo?.premiumOrganizationName != null
  const userEmail = authInfo.email || authInfo.username || ''

  const [itemFollowState, setItemFollowState] = useState(false)
  const [followingType, setFollowingType] = useState<string>()
  const [sharedPortfolioSolutionsForFollow, setSharedPortfolioSolutionsForFollow] = useState<
    SharedPortfolioSolutionEntry[]
  >([])
  const [hasLoadedFollowSearch, setHasLoadedFollowSearch] = useState(false)
  const [loadSharedPortfolios, { data }] = useGetSharedPortfoliosForMyOrganizationLazyQuery()
  const [loadSolutionFollowSearch] = useSearchSolutionsLazyQuery()

  const fetchSolutionFollowSearch = useCallback(async () => {
    if (!solutionDocumentId) return

    const authHeader = authInfo.idToken ? { Authorization: `Bearer ${authInfo.idToken}` } : undefined
    const result = await loadSolutionFollowSearch({
      variables: {
        query: {
          solutionID: solutionDocumentId,
          limit: 1,
          offset: 0,
        },
      },
      fetchPolicy: 'network-only',
      ...(authHeader ? { context: { headers: authHeader } } : {}),
    })

    const followItem = (result.data?.searchSolutions?.solutions || []).find(
      (item): item is ISolutionListItemFragment => !!item && String(item.documentId) === String(solutionDocumentId),
    )

    if (!followItem) {
      setItemFollowState(false)
      setFollowingType('')
      setSharedPortfolioSolutionsForFollow([])
      setHasLoadedFollowSearch(true)
      return
    }

    const sharedSolutionsRaw = followItem.shared_portfolio_solutions as unknown
    const normalizedSharedSolutions = Array.isArray(sharedSolutionsRaw)
      ? sharedSolutionsRaw
      : sharedSolutionsRaw &&
          typeof sharedSolutionsRaw === 'object' &&
          'data' in sharedSolutionsRaw &&
          Array.isArray((sharedSolutionsRaw as { data?: unknown }).data)
        ? (sharedSolutionsRaw as { data: unknown[] }).data ?? []
        : []

    const memberSharedSolutions = normalizedSharedSolutions.filter((obj: { members?: string | null }) =>
      isAccessibleSharedPortfolioSolution(obj.members, userEmail),
    )

    const portfolioListData: SharedPortfolioSolutionEntry[] = []

    if (followItem.followedSolutionsData) {
      portfolioListData.push({
        solutionId: String(followItem.documentId || ''),
        id: `${followItem.documentId}-private`,
        followingType: followItem.followedSolutionsData.followingType || '',
        followingSolutionNote: followItem.followedSolutionsData.followingSolutionNote,
        members: '',
        name: 'My Portfolio',
        organizationId: null,
        portfolioId: null,
        sharedPortfolio: null,
        slug: '',
      })
    }

    memberSharedSolutions.forEach(
      (obj: {
        category?: string | null
        followingSolutionNote?: string | null
        members?: string | null
        name?: string | null
        organizationId?: number | string | null
        portfolioId?: number | string | null
        sharedPortfolio?: number | string | null
        slug?: string | null
      }) => {
        portfolioListData.push({
          solutionId: String(followItem.documentId || ''),
          id: `${followItem.documentId}-shared-${String(obj.portfolioId || '')}`,
          followingType: obj.category || '',
          followingSolutionNote: obj.followingSolutionNote,
          members: obj.members || '',
          name: obj.name || '',
          organizationId: obj.organizationId != null ? String(obj.organizationId) : null,
          portfolioId: obj.portfolioId != null ? String(obj.portfolioId) : null,
          sharedPortfolio: obj.sharedPortfolio != null ? String(obj.sharedPortfolio) : null,
          slug: obj.slug || '',
        })
      },
    )

    setFollowingType(resolveFollowTypeLabel(portfolioListData, followItem.followedSolutionsData?.followingType))
    setItemFollowState(followItem.follow ?? false)
    setSharedPortfolioSolutionsForFollow(portfolioListData)
    setHasLoadedFollowSearch(true)
  }, [authInfo.idToken, loadSolutionFollowSearch, solutionDocumentId, userEmail])

  useEffect(() => {
    setHasLoadedFollowSearch(false)
  }, [solutionDocumentId])

  useEffect(() => {
    if (!routerReady || !solutionDocumentId) return
    if (status === 'loading') return
    if (status === 'authenticated' && !authInfo.idToken) return
    void fetchSolutionFollowSearch()
  }, [routerReady, solutionDocumentId, authInfo.idToken, fetchSolutionFollowSearch, status])

  const apiType = undefined
  const payload = data?.sharedPortfoliosForMyOrganization

  const displayRows: PortfolioRow[] = useMemo(() => {
    const rows: PortfolioRow[] = []
    const stub = payload?.privateFollowingPortfolio
    const showPrivateOnPage = stub && (apiType === 'private_portfolio' || !apiType)
    const portfoliosHavePrivateRow = (payload?.portfolios ?? []).some(
      (p) => p?.attributes && p.attributes.isSharedPortfolio === false,
    )

    if (showPrivateOnPage && !portfoliosHavePrivateRow) {
      rows.push({
        kind: 'private',
        id: stub?.documentId || 'private_portfolio',
        name: stub?.name || 'My Private Portfolio',
        slug: stub?.slug || '',
        shared_portfolio_memberships: [],
        shared_portfolio_solutions: [],
      })
    }

    if (apiType !== 'private_portfolio') {
      const list: Array<SharedPortfolioListItem | null | undefined> = payload?.portfolios ?? []
      for (const p of list) {
        const id = p?.id
        const attributes = p?.attributes
        const name = attributes?.name
        const slug = attributes?.slug
        if (id && name) {
          rows.push({
            kind: attributes?.isSharedPortfolio === false ? 'private' : 'shared',
            id: String(id),
            name: String(name),
            slug: String(slug),
            shared_portfolio_memberships:
              (attributes?.shared_portfolio_memberships as ISharedPortfolioMembershipItemFragment[]) ?? [],
            shared_portfolio_solutions:
              (attributes?.shared_portfolio_solutions as ISharedPortfolioSolutionItemFragment[]) ?? [],
          })
        }
      }
    }

    const sharedPart = rows.filter((r) => r.kind === 'shared')
    const privatePart = rows.filter((r) => r.kind === 'private')
    return [...privatePart, ...sharedPart]
  }, [payload, apiType])

  const portfolioPickerOptions: PortfolioPickerOption[] = useMemo(() => {
    if (!hasPremiumOrganization) return []

    const create: PortfolioPickerOption = {
      kind: 'create-new',
      id: CREATE_NEW_PORTFOLIO_ID,
      title: 'Create a New Portfolio',
      subtitle: 'Create a new shared solutions portfolio.',
    }
    const fromRows: PortfolioPickerOption[] = displayRows.map((r) => ({
      kind: 'portfolio',
      id: r.id,
      title: r.name,
      slug: r.slug,
      visibility: r.kind,
      subtitle: r.kind === 'private' ? 'These solutions are visible only to you.' : 'Shared with your organization.',
      shared_portfolio_memberships: r.shared_portfolio_memberships ?? [],
      shared_portfolio_solutions: r.shared_portfolio_solutions ?? [],
    }))
    return [create, ...fromRows]
  }, [displayRows, hasPremiumOrganization])

  useEffect(() => {
    if (!hasPremiumOrganization || !routerReady) return
    void loadSharedPortfolios({
      variables: { page: 1, pageSize: 1000, type: apiType },
      fetchPolicy: 'network-only',
    })
  }, [hasPremiumOrganization, routerReady, apiType, loadSharedPortfolios])

  const refreshSharedPortfolios = useCallback(
    async (isSharedPortfolioEntry = false) => {
      await fetchSolutionFollowSearch()
      if (!isSharedPortfolioEntry || !hasPremiumOrganization) return
      await loadSharedPortfolios({
        variables: { page: 1, pageSize: 1000, type: apiType },
        fetchPolicy: 'network-only',
      })
    },
    [fetchSolutionFollowSearch, hasPremiumOrganization, apiType, loadSharedPortfolios],
  )

  return {
    itemFollowState,
    followingType,
    sharedPortfolioSolutionsForFollow,
    hasLoadedFollowSearch,
    portfolioPickerOptions,
    refreshSharedPortfolios,
  }
}
